import { KeyboardArrowUp } from "@mui/icons-material";
import { Box, Fab, Tooltip, useTheme } from "@mui/material";
import { UUID } from "crypto";
import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { AutoSizer } from "react-virtualized";
import { useAtom } from "jotai";
import { QuestionCanAcceptFeedback, QuestionIsLoadingAtom, SessionThreadAtom } from "../state/DeepchatState";
import { scrollQuestionIntoView } from "../../../utils/scrollQuestionIntoView";
import { clarifyTurnToResponse, readClarifyTurn } from "../../../store/home/clarifyTurnPersistence";
import { useFilterContext } from "../../../contexts/FilterProvider";
import useDeepchat from "../../../contexts/useDeepchat";
import QuestionInput from "../AskQuestion/QuestionInput";
import SessionResponseItem from "./SessionResponseItem";

export default function SessionThread() {
  const Deepchat = useDeepchat();
  const { applyFilters } = useFilterContext();
  const location = useLocation();
  const [session, setSession] = useAtom(SessionThreadAtom);
  const [, setAllowFeedback] = useAtom(QuestionCanAcceptFeedback);
  const [, setLoading] = useAtom(QuestionIsLoadingAtom);
  const prevSessionLengthRef = useRef(session.length);
  const theme = useTheme();

  // Scroll to the latest question when the session grows — both when a follow-up is added and
  // when a thread is first opened. Landing on the most recent question matches what users expect
  // from other chat products; a manual "jump to top" control is provided for reviewing the start.
  useEffect(() => {
    const currentLength = session.length;
    if (currentLength > prevSessionLengthRef.current) {
      const target = session.at(-1);
      if (target) {
        const anchorId = `${target?.user_chat_session}-${currentLength - 1}`;
        scrollQuestionIntoView(anchorId);
      }
    }

    prevSessionLengthRef.current = currentLength;
  }, [session]);

  const getThreadId = (value: string) => {
    return value ? (value as UUID) : undefined;
  };

  // -- Direct Navigation Thread loading Logic
  useEffect(() => {
    const path = location.pathname.split("/");
    const threadId = getThreadId(path.at(-1) ?? "");

    const preventLoad = location.state?.prevent_load ?? false;

    // Recover an in-flight clarification turn after a refresh.
    // If the session atom is empty and a snapshot matches the current thread,
    // rebuild the turn so the user can resume where they left off (the form
    // re-hydrates their draft selections on mount).
    if (path.at(-2) === "threads" && threadId && session.length === 0) {
      const savedTurn = readClarifyTurn();
      if (savedTurn && savedTurn.userChatSession === String(threadId)) {
        if (savedTurn.searchObject) {
          applyFilters(savedTurn.searchObject);
        }
        setSession([clarifyTurnToResponse(savedTurn)]);
        setAllowFeedback(false);
        return;
      }
    }

    // eg: /deepchat/threads/1714004561
    if (path.at(-2) === "threads" && threadId && !preventLoad) {
      setLoading(true);

      Deepchat.getSession(threadId)
        .then((data) => {
          const firstResponse = data.at(0);
          if (firstResponse?.search_object) {
            applyFilters(firstResponse.search_object);
          }

          setSession(data);
          setAllowFeedback(true);
        })
        .finally(() => setLoading(false));
    }
  }, [location.pathname]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const ThreadResponsesMemo = useMemo(() => {
    return session.map((response, i) => {
      const anchorId = `${String(response.user_chat_session)}-${i}`;
      return <SessionResponseItem key={i} anchorId={anchorId} response={response} index={i} />;
    });
  }, [session]);

  return (
    <Box flex={1} minHeight="100%" maxWidth="52rem" marginX="auto">
      <Box sx={{ px: "1em" }}>{ThreadResponsesMemo}</Box>

      {/* Manual scroll-to-top: threads open on the most recent question, so give users an
          explicit way to jump back to the start of a longer conversation. */}
      {session.length > 1 && (
        <Tooltip title="Scroll to top">
          <Fab
            size="small"
            onClick={scrollToTop}
            aria-label="Scroll to top"
            sx={{
              position: "fixed",
              bottom: "110px",
              right: "2em",
              backgroundColor: "#fff",
              color: theme.palette.text.secondary,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              "&:hover": { backgroundColor: "#fff" },
              zIndex: 2,
            }}
          >
            <KeyboardArrowUp />
          </Fab>
        </Tooltip>
      )}

      {/* Followup question container */}
      <Box sx={{ height: "84px" }} className="followup-input-base">
        <AutoSizer disableHeight>
          {({ width }) => (
            <Box
              sx={{
                position: "fixed",
                width: width,
                bottom: 0,
                backgroundImage: `linear-gradient(to bottom, rgba(250,250,250,0) 0%, ${theme.palette.background.default} 25%)`,
                py: "2em",
              }}
            >
              <QuestionInput
                placeholder="Ask a related follow-up question…"
                hideSalesData={true} // Should never be visible in SessionThread view
              />
            </Box>
          )}
        </AutoSizer>
      </Box>
    </Box>
  );
}
